import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { matchQuery } from '@tanstack/react-query';
import { queryClient } from '@/queries';
import {
  useBookResources,
  useBookSpecificResource,
  useRemoveReserveResource,
  useReserveResource,
  useSwitchClinicalSpecialist,
  useSwitchOcsConsole,
} from '@/queries/orchestrator';
import { OperationStatusResponse, OrchestratorResourcesBody } from '@/api/apis/orchestrator';
import { useCaseResourcesDataQuery, useCaseByIdQuery, usePendingResourcesBooking } from '@/queries/case';
import { resourcesQueryKeys } from '@/queries/resource/resources.keys';
import { ocsConsolesQueryKeys } from '@/queries/ocs-consoles/ocs-consoles.keys';
import { refetchRouteLogistics } from '@/queries/route-planner/utils';
import { CaseResourceType } from '@/api/apis/case';

const refetchResources = async () => {
  await queryClient.invalidateQueries({
    predicate: (query) => {
      return [
        resourcesQueryKeys.getCandidatesForAssignments(),
        resourcesQueryKeys.getAvailableDevices(),
        ocsConsolesQueryKeys.getInfiniteList(),
      ].some((key) => matchQuery({ queryKey: key }, query));
    },
  });
};

type HandleResourceAssignmentParams = {
  data: Record<string, string | string[]>;
  type: 'reserve' | 'remove' | 'book';
  withRefetch?: boolean;
  showToastError?: boolean;
};

type UseResourcesHandlersReturnType = {
  handleResourceAssignment: (data: HandleResourceAssignmentParams) => Promise<void>;
  handleSwitchResources: (
    prevId: string,
    newId: string,
    type: CaseResourceType.ClinicalSpecialist | CaseResourceType.OCSConsole | CaseResourceType.Surgeon,
  ) => Promise<void>;
  handleBookResources: () => Promise<void>;
};

type UseResourcesHandlersProps = {
  id: string;
};

export const useResourcesHandlers = ({ id }: UseResourcesHandlersProps): UseResourcesHandlersReturnType => {
  const { data: caseInfo, refetch: refetchCase } = useCaseByIdQuery(id);
  const { refetch: refetchCaseResourceData } = useCaseResourcesDataQuery(id);

  const { mutateAsync: asyncReserveResource } = useReserveResource();
  const { mutateAsync: asyncRemoveReserve } = useRemoveReserveResource();
  const { mutateAsync: asyncBookResources } = useBookResources();
  const { mutateAsync: asyncPendingResourcesBooking } = usePendingResourcesBooking();
  const { mutateAsync: asyncSwitchOcsConsole } = useSwitchOcsConsole();
  const { mutateAsync: asyncSwitchClinicalSpecialist } = useSwitchClinicalSpecialist();
  const { mutateAsync: asyncBookSpecificResource } = useBookSpecificResource();

  const handleResourceAssignment = useCallback(
    async ({ data, type, withRefetch = true, showToastError = true }: HandleResourceAssignmentParams) => {
      try {
        const body = Object.keys(data).reduce((acc, item) => {
          return {
            ...acc,
            [item]: data[item],
          };
        }, {} as Partial<OrchestratorResourcesBody>);

        if (type === 'reserve') {
          await asyncReserveResource({ caseId: id, ...body });
        }

        if (type === 'remove') {
          await asyncRemoveReserve({ caseId: id, ...body });
        }

        if (type === 'book') {
          await asyncBookSpecificResource({ caseId: id, ...body });
        }
      } catch (e) {
        if (showToastError) {
          const error = e as OperationStatusResponse;

          toast.error(error.faultedMessage);
        }

        throw e;
      } finally {
        if (['reserve', 'remove'].includes(type)) {
          await refetchResources();
        }

        if (withRefetch) {
          await refetchCase();
          await refetchCaseResourceData();
        }
      }
    },
    [asyncRemoveReserve, asyncReserveResource, asyncBookSpecificResource, id, refetchCase, refetchCaseResourceData],
  );

  const handleSwitchResources = useCallback(
    async (
      prevId: string,
      newId: string,
      type: CaseResourceType.ClinicalSpecialist | CaseResourceType.OCSConsole | CaseResourceType.Surgeon,
    ) => {
      try {
        if (type === CaseResourceType.OCSConsole) {
          await asyncSwitchOcsConsole({ caseId: id, previousOcsConsoleId: prevId, ocsConsoleId: newId });
        }

        if (type === CaseResourceType.ClinicalSpecialist) {
          await asyncSwitchClinicalSpecialist({ caseId: id, previousResourceId: prevId, resourceId: newId });
        }
      } catch (e) {
        const error = e as OperationStatusResponse;

        toast.error(error.faultedMessage);

        throw e;
      } finally {
        await refetchResources();
        await refetchCase();
        await refetchCaseResourceData();

        if (caseInfo?.logisticsReference?.id) {
          await refetchRouteLogistics(caseInfo.logisticsReference.id);
        }
      }
    },
    [
      id,
      caseInfo?.logisticsReference?.id,
      asyncSwitchClinicalSpecialist,
      asyncSwitchOcsConsole,
      refetchCaseResourceData,
      refetchCase,
    ],
  );

  const handleBookResources = useCallback(async () => {
    try {
      await asyncBookResources({ caseId: id });

      await asyncPendingResourcesBooking({ caseId: id });
    } catch (e) {
      const error = e as OperationStatusResponse;

      toast.error(error.faultedMessage);

      throw e;
    } finally {
      await refetchCase();
      await refetchCaseResourceData();
    }
  }, [asyncPendingResourcesBooking, asyncBookResources, id, refetchCase, refetchCaseResourceData]);

  return {
    handleResourceAssignment,
    handleSwitchResources,
    handleBookResources,
  };
};
